// V5++ mobile UX pass — integrated selection-status strip.
//
// On mobile the floating HUD context panel (.hud__context, bottom-right
// corner) and the MobileActionBar (bottom-center) are visually disconnected:
// to read the selected squad's HP the player has to look bottom-right, but
// the verbs (Attack/Stop/Q/E) are in the bottom-center bar. The eye and
// thumb travel between two panels. This strip eliminates that by hosting
// the selection info DIRECTLY ABOVE the action bar — both panels share the
// same horizontal centre so the player reads "what's selected → which
// verbs apply" in one glance.
//
// The strip is mobile-only (CSS @media gate). On desktop it is hidden and
// the original floating HUD context panel carries selection info.
//
// Modes:
//   Squad selected (own):  portrait + name + HP bar + HP text
//   Core / Hub selected:   title + signal/squads + queue progress
//   Empty + own squads:    "⊟ Select All Squads (n)" button (dock-position
//                           replacement for the HudPanel empty-state CTA)
//   Otherwise:             hidden
//
// Rule 2 (presentation only): reads MatchState + Selection, fires intents
// through the InputRouter trigger* hooks. No sim mutations.

import type {
  Core,
  MatchState,
  PlayerId,
  ProductionHub,
  Squad,
  SquadArchetype,
} from '@signalclash/shared';
import {
  SQUAD_DEFS,
  isOperational,
} from '@signalclash/shared';

import type { InputRouter } from '../input/InputRouter.js';
import type { Selection, SelectionRef } from '../input/Selection.js';

const STRIP_ID = 'mobile-status-strip';
const STYLE_ID = 'mobile-status-strip-styles';

const ARCHETYPE_GLYPH: Record<SquadArchetype, string> = {
  recon: '◇',
  assault: '⚔',
  engineer: '⚒',
  shield: '◈',
};

interface MobileStatusStripOptions {
  selection: Selection;
  getRouter: () => InputRouter | null;
  playerId: PlayerId;
}

export class MobileStatusStrip {
  private opts: MobileStatusStripOptions;
  private container: HTMLDivElement;
  // Squad-mode children
  private squadEl: HTMLDivElement;
  private squadPortrait: HTMLDivElement;
  private squadName: HTMLDivElement;
  private squadHpFill: HTMLDivElement;
  private squadHpText: HTMLDivElement;
  // Core / Hub mode children
  private coreEl: HTMLDivElement;
  private coreTitle: HTMLDivElement;
  private coreStats: HTMLDivElement;
  private coreQueue: HTMLDivElement;
  // Select-all CTA (own-squads-but-no-selection mode)
  private selectAllBtn: HTMLButtonElement;
  private state: MatchState | null = null;
  private selectionRef: SelectionRef | null = null;
  private unsubscribeSelection: (() => void) | null = null;

  constructor(opts: MobileStatusStripOptions) {
    this.opts = opts;
    injectStyles();
    this.container = ensureDiv(STRIP_ID, 'mss');

    // Squad row
    this.squadEl = document.createElement('div');
    this.squadEl.className = 'mss__row mss__row--squad';
    this.squadPortrait = document.createElement('div');
    this.squadPortrait.className = 'mss__portrait';
    this.squadName = document.createElement('div');
    this.squadName.className = 'mss__name';
    const hpWrap = document.createElement('div');
    hpWrap.className = 'mss__hp-wrap';
    const hpBar = document.createElement('div');
    hpBar.className = 'mss__hp-bar';
    this.squadHpFill = document.createElement('div');
    this.squadHpFill.className = 'mss__hp-fill';
    hpBar.appendChild(this.squadHpFill);
    this.squadHpText = document.createElement('div');
    this.squadHpText.className = 'mss__hp-text';
    hpWrap.appendChild(hpBar);
    hpWrap.appendChild(this.squadHpText);
    this.squadEl.appendChild(this.squadPortrait);
    this.squadEl.appendChild(this.squadName);
    this.squadEl.appendChild(hpWrap);

    // Core / Hub row
    this.coreEl = document.createElement('div');
    this.coreEl.className = 'mss__row mss__row--core';
    this.coreTitle = document.createElement('div');
    this.coreTitle.className = 'mss__core-title';
    this.coreStats = document.createElement('div');
    this.coreStats.className = 'mss__core-stats';
    this.coreQueue = document.createElement('div');
    this.coreQueue.className = 'mss__core-queue';
    this.coreEl.appendChild(this.coreTitle);
    this.coreEl.appendChild(this.coreStats);
    this.coreEl.appendChild(this.coreQueue);

    // Select-all CTA
    this.selectAllBtn = document.createElement('button');
    this.selectAllBtn.type = 'button';
    this.selectAllBtn.className = 'mss__select-all';
    this.selectAllBtn.addEventListener('click', () => {
      this.opts.getRouter()?.triggerSelectAll();
    });

    this.container.appendChild(this.squadEl);
    this.container.appendChild(this.coreEl);
    this.container.appendChild(this.selectAllBtn);

    this.unsubscribeSelection = opts.selection.subscribe((sel) => {
      this.selectionRef = sel;
      this.render();
    });
    this.hide();
  }

  setPlayerId(playerId: PlayerId): void {
    this.opts = { ...this.opts, playerId };
    this.render();
  }

  show(): void {
    this.container.style.display = '';
    this.render();
  }

  hide(): void {
    this.container.style.display = 'none';
  }

  update(state: MatchState): void {
    this.state = state;
    this.render();
  }

  dispose(): void {
    this.unsubscribeSelection?.();
    this.unsubscribeSelection = null;
    this.container.remove();
  }

  private render(): void {
    const sel = this.selectionRef;
    const state = this.state;
    if (state === null) {
      this.applyMode('hidden');
      return;
    }
    if (sel !== null && sel.kind === 'squad') {
      const squad = state.squads[sel.id];
      if (squad !== undefined && squad.ownerId === this.opts.playerId) {
        this.renderSquad(squad);
        return;
      }
    }
    if (sel !== null && sel.kind === 'core') {
      const core = state.cores[sel.id];
      if (core !== undefined && core.ownerId === this.opts.playerId) {
        this.renderCore(core, state);
        return;
      }
    }
    if (sel !== null && sel.kind === 'building') {
      const building = state.buildings[sel.id];
      if (
        building !== undefined &&
        building.ownerId === this.opts.playerId &&
        building.kind === 'production_hub'
      ) {
        this.renderHub(building, state);
        return;
      }
    }
    // No own producer / squad selected — surface the Select-All CTA if the
    // player has multiple squads they could grab in one tap. Single-squad
    // case stays hidden because tapping the squad directly is just as fast.
    let ownSquads = 0;
    for (const s of Object.values(state.squads)) {
      if (s.ownerId === this.opts.playerId) ownSquads += 1;
    }
    if (ownSquads >= 2) {
      this.renderSelectAll(ownSquads);
      return;
    }
    this.applyMode('hidden');
  }

  private renderSquad(squad: Squad): void {
    this.applyMode('squad');
    const ratio = squad.maxHp > 0 ? Math.max(0, Math.min(1, squad.hp / squad.maxHp)) : 0;
    this.squadPortrait.dataset.archetype = squad.archetype;
    this.squadPortrait.textContent = ARCHETYPE_GLYPH[squad.archetype];
    this.squadName.textContent = capitalize(squad.archetype);
    this.squadHpFill.style.width = `${(ratio * 100).toFixed(1)}%`;
    this.squadHpFill.dataset.low = ratio < 0.4 ? 'true' : 'false';
    this.squadHpText.textContent = `${Math.ceil(squad.hp)} / ${squad.maxHp}`;
  }

  private renderCore(core: Core, state: MatchState): void {
    // V1.7 (2.12 design fix): Hubs ADD a parallel queue, so the Core keeps
    // producing while Hubs exist. The "Hub Active" branch is gone.
    this.applyMode('core');
    const player = state.players[this.opts.playerId];
    this.coreTitle.textContent = 'Core · Production';
    this.coreStats.textContent =
      `${Math.floor(player.signal)} S · ${player.squadCount}/${player.squadCap}`;
    this.renderQueue(core.produces);
  }

  private renderHub(hub: ProductionHub, state: MatchState): void {
    this.applyMode('core');
    const player = state.players[this.opts.playerId];
    if (!isOperational(hub)) {
      this.coreTitle.textContent = 'Hub · Constructing';
      this.coreStats.textContent =
        `${(hub.buildProgress * 100).toFixed(0)}% · ${Math.ceil(hub.hp)}/${hub.maxHp} HP`;
      this.coreQueue.textContent = '';
      this.coreQueue.dataset.active = 'false';
      return;
    }
    this.coreTitle.textContent = 'Production Hub';
    this.coreStats.textContent =
      `${Math.floor(player.signal)} S · ${player.squadCount}/${player.squadCap}`;
    this.renderQueue(hub.produces);
  }

  private renderQueue(
    slot: { queue: SquadArchetype[]; progress: number } | null,
  ): void {
    if (slot === null || slot.queue.length === 0) {
      this.coreQueue.textContent = 'Queue empty';
      this.coreQueue.dataset.active = 'false';
      this.coreQueue.dataset.archetype = '';
      return;
    }
    const head = slot.queue[0]!;
    const headDef = SQUAD_DEFS[head];
    const remainSec = Math.max(0, headDef.buildTimeSec * (1 - slot.progress)).toFixed(1);
    const tail = slot.queue.length > 1 ? ` (+${slot.queue.length - 1})` : '';
    this.coreQueue.textContent = `${capitalize(head)} ${remainSec}s${tail}`;
    this.coreQueue.dataset.active = 'true';
    this.coreQueue.dataset.archetype = head;
  }

  private renderSelectAll(n: number): void {
    this.applyMode('selectAll');
    this.selectAllBtn.textContent = `⊟ Select All Squads (${n})`;
  }

  private applyMode(which: 'squad' | 'core' | 'selectAll' | 'hidden'): void {
    if (which === 'hidden') {
      this.container.style.display = 'none';
      return;
    }
    this.container.style.display = '';
    this.squadEl.style.display = which === 'squad' ? '' : 'none';
    this.coreEl.style.display = which === 'core' ? '' : 'none';
    this.selectAllBtn.style.display = which === 'selectAll' ? '' : 'none';
  }
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

function ensureDiv(id: string, className: string): HTMLDivElement {
  const existing = document.getElementById(id);
  if (existing instanceof HTMLDivElement) {
    existing.className = className;
    return existing;
  }
  const div = document.createElement('div');
  div.id = id;
  div.className = className;
  document.body.appendChild(div);
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${STRIP_ID} {
  position: fixed;
  display: none;
  z-index: 17;
  left: 50%;
  transform: translateX(-50%);
  /* Sits directly above the MobileActionBar (which is at bottom:8 ~52px tall)
   * with a small gap. iOS home-indicator safe-area is added in. */
  bottom: calc(66px + env(safe-area-inset-bottom, 0px));
  padding: 6px 10px;
  background: rgba(8,12,18,0.88);
  border: 1px solid #2c3a48;
  border-radius: 6px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #cfd6dd;
  box-shadow: 0 6px 22px rgba(0,0,0,0.55);
  /* Keep the strip narrow enough that it doesn't overlap the minimap on
   * the left (124 px) or the right gutter. Center-aligned via transform. */
  max-width: calc(100vw - 156px);
  pointer-events: auto;
}
.mss__row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
}
/* Squad row: portrait | name | HP bar | HP text  */
.mss__row--squad { min-width: 220px; }
.mss__portrait {
  width: 26px; height: 26px;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; font-weight: 700; line-height: 1;
  border: 1px solid #3a4c60;
  background: linear-gradient(180deg, rgba(36,46,60,0.95), rgba(20,28,38,0.95));
  box-shadow: inset 0 0 6px rgba(106,216,255,0.18);
  flex: 0 0 auto;
}
.mss__portrait[data-archetype="recon"]    { color: #6ad8ff; border-color: #2a5070; }
.mss__portrait[data-archetype="assault"]  { color: #ff8060; border-color: #60281a; }
.mss__portrait[data-archetype="engineer"] { color: #ffe680; border-color: #4a4010; }
.mss__portrait[data-archetype="shield"]   { color: #4ad0b8; border-color: #1a5048; }
.mss__name {
  font-size: 11px; letter-spacing: 0.08em;
  color: #eef3f8; text-transform: uppercase;
  flex: 0 0 auto;
}
.mss__hp-wrap {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 80px;
}
.mss__hp-bar {
  flex: 1;
  height: 6px;
  background: #171a1f;
  border: 1px solid #2a3038;
  border-radius: 2px;
  overflow: hidden;
}
.mss__hp-fill {
  height: 100%; width: 100%;
  background: #4ad06b;
  transition: width 80ms linear;
}
.mss__hp-fill[data-low="true"] { background: #e2543c; }
.mss__hp-text {
  font-size: 10px;
  color: #cfd6dd;
  min-width: 60px;
  text-align: right;
}
/* Core / Hub row: title · stats · queue progress */
.mss__row--core { min-width: 220px; }
.mss__core-title {
  font-size: 10px; letter-spacing: 0.12em;
  color: #6ad8ff; text-transform: uppercase;
  flex: 0 0 auto;
}
.mss__core-stats {
  font-size: 11px;
  color: #cfd6dd;
  flex: 0 0 auto;
}
.mss__core-queue {
  font-size: 10px;
  color: #8c95a0;
  flex: 1 1 auto;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mss__core-queue[data-active="true"] { color: #f5c842; }
.mss__core-queue[data-archetype="recon"]    { color: #6ad8ff; }
.mss__core-queue[data-archetype="assault"]  { color: #ff8060; }
.mss__core-queue[data-archetype="engineer"] { color: #ffe680; }
.mss__core-queue[data-archetype="shield"]   { color: #4ad0b8; }
/* Select-all CTA */
.mss__select-all {
  font-family: inherit;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 6px 14px;
  background: linear-gradient(180deg, rgba(36,46,60,0.95), rgba(20,28,38,0.95));
  color: #f5e76e;
  border: 1px solid #c8b840;
  border-radius: 4px;
  cursor: pointer;
  min-height: 32px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 0 6px rgba(245,231,110,0.2);
}
.mss__select-all:active { transform: scale(0.96); background: #2c2818; }
/* Show only on mobile / coarse-pointer / narrow viewports — the floating
 * HUD context panel handles desktop. */
@media (max-width: 820px), (pointer: coarse) {
  #${STRIP_ID} { display: flex; }
}
@media (max-width: 460px) {
  #${STRIP_ID} { max-width: calc(100vw - 140px); padding: 4px 8px; }
  .mss__portrait { width: 22px; height: 22px; font-size: 14px; }
  .mss__name { font-size: 10px; }
  .mss__hp-text { font-size: 9px; min-width: 54px; }
  .mss__core-title { font-size: 9px; }
  .mss__core-stats { font-size: 10px; }
  .mss__core-queue { font-size: 9px; }
  .mss__select-all { font-size: 11px; padding: 5px 12px; min-height: 30px; }
}
@media (orientation: landscape) and (max-height: 480px) and (pointer: coarse) {
  #${STRIP_ID} {
    bottom: calc(52px + env(safe-area-inset-bottom, 0px));
    padding: 3px 8px;
  }
  .mss__portrait { width: 20px; height: 20px; font-size: 12px; }
}
`;
  document.head.appendChild(style);
}
